import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  SetupDataTable,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormTextarea } from '../components/FormInput';
import { useApi } from '../../../api/useApi';

interface SetupPlatformUsersProps {
  brand: BrandConfig;
  Icons: IconsType;
}

// User type from Auth0 API
interface Auth0User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  lastLogin?: string;
  blocked: boolean;
  status: 'pending' | 'approved' | 'suspended';
  appMetadata: Record<string, unknown>;
  organizations?: Array<{ id: string; name: string; displayName?: string }>;
}

// User detail type with additional fields
interface Auth0UserDetail extends Auth0User {
  picture?: string;
  loginsCount?: number;
  emailVerified?: boolean;
  userMetadata?: Record<string, unknown>;
}

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'suspended', label: 'Suspended' },
];

type TabType = 'profile' | 'metadata' | 'actions';

const SetupPlatformUsers: React.FC<SetupPlatformUsersProps> = ({ brand, Icons }) => {
  const { get, post, patch } = useApi();
  const [users, setUsers] = useState<Auth0User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<Auth0UserDetail | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Metadata form
  const [metadataForm, setMetadataForm] = useState<{
    notes?: string;
    accountNumber?: string;
    company?: string;
  }>({});

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const data = await get<{ users: Auth0User[] }>(`/api/admin/users?${params}`);
      // Filter by search client-side (API doesn't support text search well)
      let fetchedUsers = data.users || [];
      if (search) {
        const searchLower = search.toLowerCase();
        fetchedUsers = fetchedUsers.filter((u: Auth0User) =>
          u.email?.toLowerCase().includes(searchLower) ||
          u.name?.toLowerCase().includes(searchLower)
        );
      }
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get, search, statusFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchUsers();
  }, [fetchUsers]);

  const fetchUserDetail = async (userId: string) => {
    try {
      const data = await get<Auth0UserDetail>(`/api/admin/users/${encodeURIComponent(userId)}`);
      setSelectedUser(data);
      // Populate metadata form
      setMetadataForm({
        notes: (data.appMetadata?.notes as string) || '',
        accountNumber: (data.appMetadata?.accountNumber as string) || '',
        company: (data.appMetadata?.company as string) || '',
      });
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
    }
  };

  const handleRowClick = async (user: Auth0User) => {
    setActiveTab('profile');
    setActionResult(null);
    setIsDrawerOpen(true);
    await fetchUserDetail(user.id);
  };

  const handleAction = async (action: 'approve' | 'suspend' | 'reactivate' | 'reject' | 'password-reset') => {
    if (!selectedUser) return;

    setIsSaving(true);
    setActionResult(null);
    try {
      const data = await post<{ success: boolean; message?: string }>(
        `/api/admin/users/${encodeURIComponent(selectedUser.id)}/${action}`,
        {}
      );
      setActionResult({ type: 'success', message: data.message || 'Action completed successfully' });
      // Refresh user detail and list
      await fetchUserDetail(selectedUser.id);
      fetchUsers();
    } catch (error) {
      setActionResult({ type: 'error', message: error instanceof Error ? error.message : 'Action failed' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMetadata = async () => {
    if (!selectedUser) return;

    setIsSaving(true);
    setActionResult(null);
    try {
      await patch(`/api/admin/users/${encodeURIComponent(selectedUser.id)}`, {
        appMetadata: metadataForm,
      });
      setActionResult({ type: 'success', message: 'Metadata saved successfully' });
      await fetchUserDetail(selectedUser.id);
    } catch (error) {
      setActionResult({ type: 'error', message: error instanceof Error ? error.message : 'Failed to save metadata' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleMetadataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setMetadataForm(prev => ({ ...prev, [name]: value }));
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    {
      key: 'email',
      header: 'Email',
      render: (user: Auth0User) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: brand.colors.link }}>
            {user.email}
          </span>
          <SetupItemPreviewPopover
            brand={brand}
            title={user.name || user.email}
            fields={[
              { label: 'Email', value: user.email },
              { label: 'Name', value: user.name || '-' },
              { label: 'Status', value: user.status },
              { label: 'Created', value: formatDate(user.createdAt) },
              { label: 'Last Login', value: user.lastLogin ? formatDate(user.lastLogin) : 'Never' },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (user: Auth0User) => (
        <div>
          <div className="font-medium" style={{ color: brand.colors.text }}>
            {user.name || '-'}
          </div>
          {user.organizations && user.organizations.length > 0 && (
            <div className="text-xs" style={{ color: brand.colors.textLight }}>
              {user.organizations.map(o => o.displayName || o.name).join(', ')}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (user: Auth0User) => (
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          {formatDate(user.createdAt)}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      render: (user: Auth0User) => (
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          {formatDate(user.lastLogin)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: Auth0User) => <StatusBadge status={user.status} />,
    },
  ];

  const tabs: { key: TabType; label: string }[] = [
    { key: 'profile', label: 'Profile' },
    { key: 'metadata', label: 'Metadata' },
    { key: 'actions', label: 'Actions' },
  ];

  return (
    <div>
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search users by email or name..."
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusChange={setStatusFilter}
      />

      <SetupDataTable
        brand={brand}
        Icons={Icons}
        columns={columns}
        data={users}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No users found"
        getRowId={(user) => user.id}
      />

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="User Details"
        subtitle={selectedUser?.email}
        brand={brand}
        Icons={Icons}
        width="lg"
      >
        {!selectedUser ? (
          <div className="flex items-center justify-center py-12">
            <div
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
            />
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div
              className="flex border-b mb-6 -mx-6 px-6"
              style={{ borderColor: brand.colors.border }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === tab.key ? '' : 'border-transparent'
                  }`}
                  style={{
                    color: activeTab === tab.key ? brand.colors.link : brand.colors.textLight,
                    borderColor: activeTab === tab.key ? brand.colors.primary : 'transparent',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Action Result Alert */}
            {actionResult && (
              <div
                className={`p-4 rounded-lg mb-6 ${
                  actionResult.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}
              >
                {actionResult.message}
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  {selectedUser.picture ? (
                    <img
                      src={selectedUser.picture}
                      alt={selectedUser.name || 'User'}
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-medium"
                      style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                    >
                      {selectedUser.name?.[0] || selectedUser.email?.[0] || '?'}
                    </div>
                  )}
                  <div>
                    <div className="text-lg font-medium" style={{ color: brand.colors.text }}>
                      {selectedUser.name || 'No name'}
                    </div>
                    <div className="text-sm" style={{ color: brand.colors.textLight }}>
                      {selectedUser.email}
                    </div>
                    <div className="mt-2">
                      <StatusBadge status={selectedUser.status} />
                    </div>
                  </div>
                </div>

                <div
                  className="border-t pt-4"
                  style={{ borderColor: brand.colors.border }}
                >
                  <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                    Account Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs" style={{ color: brand.colors.textLight }}>User ID</div>
                      <div className="text-sm font-mono" style={{ color: brand.colors.text }}>
                        {selectedUser.id}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: brand.colors.textLight }}>Email Verified</div>
                      <div className="text-sm" style={{ color: brand.colors.text }}>
                        {selectedUser.emailVerified ? 'Yes' : 'No'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: brand.colors.textLight }}>Created</div>
                      <div className="text-sm" style={{ color: brand.colors.text }}>
                        {formatDate(selectedUser.createdAt)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: brand.colors.textLight }}>Last Login</div>
                      <div className="text-sm" style={{ color: brand.colors.text }}>
                        {formatDate(selectedUser.lastLogin)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: brand.colors.textLight }}>Login Count</div>
                      <div className="text-sm" style={{ color: brand.colors.text }}>
                        {selectedUser.loginsCount ?? 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: brand.colors.textLight }}>Blocked</div>
                      <div className="text-sm" style={{ color: brand.colors.text }}>
                        {selectedUser.blocked ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedUser.organizations && selectedUser.organizations.length > 0 && (
                  <div
                    className="border-t pt-4"
                    style={{ borderColor: brand.colors.border }}
                  >
                    <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                      Organizations
                    </h3>
                    <div className="space-y-2">
                      {selectedUser.organizations.map((org) => (
                        <div
                          key={org.id}
                          className="p-3 rounded-lg"
                          style={{ backgroundColor: brand.colors.bgLight }}
                        >
                          <div className="font-medium text-sm" style={{ color: brand.colors.text }}>
                            {org.displayName || org.name}
                          </div>
                          <div className="text-xs font-mono" style={{ color: brand.colors.textLight }}>
                            {org.id}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Metadata Tab */}
            {activeTab === 'metadata' && (
              <div className="space-y-6">
                <FormInput
                  brand={brand}
                  label="Account Number"
                  name="accountNumber"
                  value={metadataForm.accountNumber || ''}
                  onChange={handleMetadataChange}
                  placeholder="e.g., ACC-001"
                />

                <FormInput
                  brand={brand}
                  label="Company"
                  name="company"
                  value={metadataForm.company || ''}
                  onChange={handleMetadataChange}
                  placeholder="Company name"
                />

                <FormTextarea
                  brand={brand}
                  label="Notes"
                  name="notes"
                  value={metadataForm.notes || ''}
                  onChange={handleMetadataChange}
                  rows={4}
                  placeholder="Internal notes about this user..."
                />

                <div
                  className="border-t pt-4"
                  style={{ borderColor: brand.colors.border }}
                >
                  <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                    Raw App Metadata
                  </h3>
                  <pre
                    className="p-4 rounded-lg text-xs overflow-auto"
                    style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
                  >
                    {JSON.stringify(selectedUser.appMetadata, null, 2)}
                  </pre>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleSaveMetadata}
                    disabled={isSaving}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                  >
                    {isSaving ? 'Saving...' : 'Save Metadata'}
                  </button>
                </div>
              </div>
            )}

            {/* Actions Tab */}
            {activeTab === 'actions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                    User Actions
                  </h3>
                  <div className="space-y-3">
                    {selectedUser.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleAction('approve')}
                          disabled={isSaving}
                          className="w-full p-4 rounded-lg border text-left transition-colors hover:bg-green-50"
                          style={{ borderColor: brand.colors.border }}
                        >
                          <div className="font-medium text-green-700">Approve User</div>
                          <div className="text-sm text-green-600">
                            Allow this user to access the platform
                          </div>
                        </button>
                        <button
                          onClick={() => handleAction('reject')}
                          disabled={isSaving}
                          className="w-full p-4 rounded-lg border text-left transition-colors hover:bg-red-50"
                          style={{ borderColor: brand.colors.border }}
                        >
                          <div className="font-medium text-red-700">Reject User</div>
                          <div className="text-sm text-red-600">
                            Delete this user from the system
                          </div>
                        </button>
                      </>
                    )}

                    {selectedUser.status === 'approved' && (
                      <button
                        onClick={() => handleAction('suspend')}
                        disabled={isSaving}
                        className="w-full p-4 rounded-lg border text-left transition-colors hover:bg-yellow-50"
                        style={{ borderColor: brand.colors.border }}
                      >
                        <div className="font-medium text-yellow-700">Suspend User</div>
                        <div className="text-sm text-yellow-600">
                          Block this user from logging in
                        </div>
                      </button>
                    )}

                    {selectedUser.status === 'suspended' && (
                      <button
                        onClick={() => handleAction('reactivate')}
                        disabled={isSaving}
                        className="w-full p-4 rounded-lg border text-left transition-colors hover:bg-green-50"
                        style={{ borderColor: brand.colors.border }}
                      >
                        <div className="font-medium text-green-700">Reactivate User</div>
                        <div className="text-sm text-green-600">
                          Unblock this user and allow login
                        </div>
                      </button>
                    )}

                    <button
                      onClick={() => handleAction('password-reset')}
                      disabled={isSaving}
                      className="w-full p-4 rounded-lg border text-left transition-colors hover:bg-blue-50"
                      style={{ borderColor: brand.colors.border }}
                    >
                      <div className="font-medium text-blue-700">Send Password Reset</div>
                      <div className="text-sm text-blue-600">
                        Send a password reset email to this user
                      </div>
                    </button>
                  </div>
                </div>

                <div
                  className="border-t pt-4"
                  style={{ borderColor: brand.colors.border }}
                >
                  <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                    Current Status
                  </h3>
                  <div
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: brand.colors.bgLight }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium" style={{ color: brand.colors.text }}>
                          {selectedUser.status === 'pending' && 'Pending Approval'}
                          {selectedUser.status === 'approved' && 'Active User'}
                          {selectedUser.status === 'suspended' && 'Suspended'}
                        </div>
                        <div className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
                          {selectedUser.status === 'pending' && 'This user is waiting for admin approval'}
                          {selectedUser.status === 'approved' && 'This user can access the platform'}
                          {selectedUser.status === 'suspended' && 'This user is blocked from logging in'}
                        </div>
                      </div>
                      <StatusBadge status={selectedUser.status} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupPlatformUsers;
